import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Filter,
  Search,
  Shield,
  User,
  XCircle,
} from 'lucide-react'

import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { PageHeader } from '@/components/atoms'

import { LogEntry } from '@/types/system'

export const ActivityLog = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')

  // Mock Data
  const logs: LogEntry[] = [
    {
      id: 'log-1',
      userId: 'u1',
      userName: 'Admin User',
      action: 'update',
      resourceType: 'product',
      resourceId: 'p123',
      details: "Updated stock quantity for 'Wireless Headphones'",
      ipAddress: '192.168.1.1',
      userAgent: 'Chrome/120.0.0.0',
      status: 'success',
      createdAt: new Date('2024-06-12T10:30:00'),
    },
    {
      id: 'log-2',
      userId: 'u2',
      userName: 'John Manager',
      action: 'login',
      resourceType: 'auth',
      details: 'Successful login',
      ipAddress: '10.0.0.5',
      userAgent: 'Firefox/118.0',
      status: 'success',
      createdAt: new Date('2024-06-12T09:15:00'),
    },
    {
      id: 'log-3',
      userId: 'u3',
      userName: 'System',
      action: 'backup',
      resourceType: 'database',
      details: 'Daily backup completed',
      ipAddress: 'localhost',
      userAgent: 'Server-Worker',
      status: 'success',
      createdAt: new Date('2024-06-12T02:00:00'),
    },
    {
      id: 'log-4',
      userId: 'u1',
      userName: 'Admin User',
      action: 'delete',
      resourceType: 'order',
      resourceId: 'ord-999',
      details: 'Attempted to delete archived order',
      ipAddress: '192.168.1.1',
      userAgent: 'Chrome/120.0.0.0',
      status: 'failure',
      createdAt: new Date('2024-06-11T16:45:00'),
    },
  ]

  const getStatusIcon = (status: LogEntry['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="size-4 text-success" />
      case 'failure':
        return <XCircle className="size-4 text-error" />
      case 'warning':
        return <AlertTriangle className="size-4 text-warning" />
    }
  }

  return (
    <div>
      <PageHeader
        size="large"
        title="Activity log"
        actions={
          <Button variant="secondary">
            <Download />
            Export
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Events</CardTitle>
          <CardDescription className="text-text-2">System events and staff actions.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search logs"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-44">
                <div className="flex items-center gap-2">
                  <Filter className="size-4" />
                  <SelectValue placeholder="Filter by type" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All events</SelectItem>
                <SelectItem value="auth">Authentication</SelectItem>
                <SelectItem value="product">Product</SelectItem>
                <SelectItem value="order">Order</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>IP address</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <span className="text-sm capitalize">{log.status}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="flex size-6 items-center justify-center rounded-full bg-fill">
                        <User className="size-3" />
                      </div>
                      <span className="text-sm font-medium text-ink">{log.userName}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge tone="soft" className="capitalize">
                      {log.action}
                    </Badge>
                    <span className="ml-2 text-xs capitalize text-text-2">
                      {log.resourceType}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-72">
                    <p className="truncate text-sm" title={log.details}>
                      {log.details}
                    </p>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-text-2">
                    {log.ipAddress}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm tabular-nums text-text-2">
                    {log.createdAt.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
